import type { StipulationActionRequest, StipulationActionResponse } from "@credit-acceptance/step6-bff-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { workbenchStipulationsApi } from "../../../api/client";
import { useForceExitStore } from "../../../stores/forceExitStore";
import { mapStipulationItem } from "./mapStipulations";
import { patchStipulationListsForApproval } from "./patchStipulationLists";
import type { StipulationListView, StipulationView } from "./types";

export interface ApplyStipulationActionInput {
  stipulationId: string;
  stipCode: string;
  stipulationAppliedId: number;
  actionCode: string;
  reasonPk?: number;
  noteText?: string;
  denialReason?: string;
}

/**
 * Patches a cached stipulation list with the saved row from the action response.
 * @param current - Cached list to patch.
 * @param rowId - Panel-assigned id of the row that was saved.
 * @param response - BFF action response, carrying the updated row in `stip`.
 * @param openOnly - Whether this cache entry is filtered to open stips only.
 */
function patchListForAction(
  current: StipulationListView,
  rowId: string,
  response: StipulationActionResponse,
  openOnly?: boolean
): StipulationListView {
  const rowIndex = current.items.findIndex((item) => item.id === rowId);
  if (rowIndex < 0 || !response.stip) {
    return current;
  }

  const existing = current.items[rowIndex];
  const stip = response.stip;
  const mapped = mapStipulationItem(stip, rowIndex);
  // The actions endpoint may return a narrower projection than the GET list
  // (e.g. omitting history/recentNote/description); fall back to the cached
  // row for those fields instead of letting a missing field wipe them.
  const nextItem: StipulationView = {
    ...mapped,
    id: existing.id,
    description: stip.description ?? existing.description,
    policyTag: stip.policyTag ?? existing.policyTag,
    categoryCode: stip.categoryCode ?? existing.categoryCode,
    workbenchScreens: stip.workbenchScreens ?? existing.workbenchScreens,
    secondaryProofsAllowed: stip.secondaryProofsAllowed ?? existing.secondaryProofsAllowed,
    isRedFlag: stip.isRedFlag ?? existing.isRedFlag,
    // The action response never carries applicants, so porExceptionInd can
    // only come from the cached row.
    porExceptionInd: existing.porExceptionInd,
    openLoanStip: stip.openLoanStip ?? existing.openLoanStip,
    recentNote: stip.recentNote ? mapped.recentNote : existing.recentNote,
    history: stip.history ? mapped.history : existing.history,
  };

  const nextItems = [...current.items];
  nextItems[rowIndex] = nextItem;

  const filteredItems =
    openOnly && nextItem.statusDisplay !== "open" ? nextItems.filter((item) => item.id !== rowId) : nextItems;

  return {
    ...current,
    items: filteredItems,
    openCount: response.stipSummary?.openCount ?? current.openCount,
    totalCount: response.stipSummary?.totalCount ?? current.totalCount,
  };
}

/**
 * Saves a selected stipulation action and patches cached stipulation lists for
 * the current approval using the BFF response.
 * @param approvalId
 */
export function useApplyStipulationAction(approvalId: string) {
  const queryClient = useQueryClient();
  const shouldForceExit = useForceExitStore((state) => state.shouldForceExit);

  return useMutation({
    mutationFn: async (input: ApplyStipulationActionInput): Promise<StipulationActionResponse> => {
      if (shouldForceExit) {
        return {} as StipulationActionResponse;
      }
      const body: StipulationActionRequest = {
        actionCode: input.actionCode,
        reasonPk: input.reasonPk,
        noteText: input.noteText,
        denialReason: input.denialReason,
      };
      const response = await workbenchStipulationsApi.applyAction(
        approvalId,
        input.stipCode,
        input.stipulationAppliedId,
        body
      );
      if (!response.data.stip) {
        throw new Error("Stipulation action response did not include the updated stip");
      }
      return response.data;
    },
    onSuccess: (response, input) => {
      patchStipulationListsForApproval(queryClient, approvalId, (data, keyArguments) =>
        patchListForAction(data, input.stipulationId, response, keyArguments.openOnly)
      );
    },
  });
}
